/* eslint-disable camelcase */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useUnityContext } from "react-unity-webgl";
import {
	type BackgammonBoardData,
	type DiceData,
	type SocketActionData,
	type SocketDiceSelectData,
	type SocketPiceMovedData,
	type SocketTurnData,
	type SocketPiceUndoData,
	type SocketResultData,
	type BackgammonPlayer,
	type DiceValues,
	BackgammonSocketEventVariant,
	CheckerPositionVariants,
	unityEvents,
} from "../../types";

import type { ReactUnityEventParameter } from "react-unity-webgl/distribution/types/react-unity-event-parameters";
import {
	getSquadFromPlayerId,
	SocketMatchDataToBackgammonBoardData,
} from "../../helpers";
import { useAblyRealtime, useSocket } from "@/hooks";
import { BACKGAMMON_GAME_PATH } from "../../Constants";
import {
	sendMessageToReactNative,
	sendUnityProfileToReactNative,
} from "@/helpers";

interface Props {
	token: string;
	matchID: string;
	clientId: string;
}

function UseBackgammon({ matchID, token, clientId }: Props) {
	const [initialized, setInitialized] = useState(false);
	const [isConnected, setIsConnected] = useState(false);

	const {
		unityProvider,
		loadingProgression,
		isLoaded,
		addEventListener,
		removeEventListener,
		sendMessage,
	} = useUnityContext({
		loaderUrl: BACKGAMMON_GAME_PATH.LOADER_URL,
		dataUrl: BACKGAMMON_GAME_PATH.DATA_URL,
		frameworkUrl: BACKGAMMON_GAME_PATH.FRAMEWORK_URL,
		codeUrl: BACKGAMMON_GAME_PATH.CODE_URL,
	});
	const loadingPercentage = useMemo(
		() => Math.round(loadingProgression * 100),
		[loadingProgression]
	);

	const { matchSocket, boardData, boardCleaner, reconnect } =
		useSocket<BackgammonBoardData>({
			token,
			matchID,
			selector: SocketMatchDataToBackgammonBoardData,
		});
	const { MatchPresentsHandler } = useAblyRealtime({
		clientID: clientId,
		matchID: matchID,
	});
	// socket connector
	useEffect(() => {
		if (isLoaded && isConnected) {
			reconnect();
		}
	}, [isLoaded, reconnect, isConnected]);
	const HandleSocketTurnUpdate = useCallback(
		(playerId: number, timer = 60000) => {
			try {
				if (boardData) {
					const playerColor = getSquadFromPlayerId(playerId, boardData);

					sendMessage("Game Manager", "changeTurn", playerColor);
					sendMessage(playerColor, "SetTimer", timer);
				}
			} catch (error) {
				console.log(error);
			}
		},
		[boardData, sendMessage]
	);
	const HandleSocketRollDice = useCallback(
		(diceValues: Array<DiceValues>) => {
			try {
				if (boardData) {
					sendMessage(
						"DiceManager",
						"RollDice",
						diceValues.map((dice) => dice.value).join(",")
					);
					matchSocket.emit("user-game-action", {
						match_id: matchID,
						action_name: "backgammon.select_dice",
						data: {
							player_id: boardData?.current_user_id,
							selected_dice:
								diceValues.find((dice) => dice.canMove)?.value ?? 0,
						},
					});
				}
			} catch (error) {
				console.log(error);
			}
		},
		[matchID, matchSocket, sendMessage, boardData]
	);
	// unity listeners
	const HandleDice = useCallback(() => {
		matchSocket.emit("user-game-action", {
			match_id: matchID,
			action_name: "backgammon.roll_dice",
		});
	}, [matchSocket, matchID]);
	const HandleMove = useCallback(
		(...parameters: Array<ReactUnityEventParameter>) => {
			const [piceID] = parameters;

			matchSocket.emit("user-game-action", {
				match_id: matchID,
				action_name: "backgammon.select_piece",
				data: {
					checker_id: piceID ? +piceID : 0,
				},
			});
		},
		[matchID, matchSocket]
	);

	const HandleDiceSelect = useCallback(
		(...parameters: Array<ReactUnityEventParameter>) => {
			const [DiceValue] = parameters;

			matchSocket.emit("user-game-action", {
				match_id: matchID,
				action_name: "backgammon.select_dice",
				data: {
					player_id: boardData?.current_user_id,
					selected_dice: DiceValue ? +DiceValue : 0,
				},
			});
		},
		[matchID, matchSocket, boardData]
	);
	const HandleUndo = useCallback(() => {
		matchSocket.emit("user-game-action", {
			match_id: matchID,
			action_name: "backgammon.undo_action",
		});
	}, [matchID, matchSocket]);
	const HandleConfirm = useCallback(() => {
		matchSocket.emit("user-game-action", {
			match_id: matchID,
			action_name: "backgammon.confirm_action",
		});
	}, [matchID, matchSocket]);
	useEffect(() => {
		if (isLoaded) {
			addEventListener(unityEvents.DICE_ROLLED, HandleDice);
			addEventListener(unityEvents.PLAYER_MOVED, HandleMove);
			addEventListener(unityEvents.DICE_SELECTED, HandleDiceSelect);
			addEventListener(
				unityEvents.PROFILE_CLICKED,
				sendUnityProfileToReactNative
			);
			addEventListener(unityEvents.UNDO, HandleUndo);
			addEventListener(unityEvents.CONFIRM, HandleConfirm);
		}

		return () => {
			removeEventListener(unityEvents.DICE_ROLLED, HandleDice);
			removeEventListener(unityEvents.PLAYER_MOVED, HandleMove);
			removeEventListener(
				unityEvents.PROFILE_CLICKED,
				sendUnityProfileToReactNative
			);
			removeEventListener(unityEvents.DICE_SELECTED, HandleDiceSelect);
			removeEventListener(unityEvents.UNDO, HandleUndo);
			removeEventListener(unityEvents.CONFIRM, HandleConfirm);
		};
	}, [
		addEventListener,
		removeEventListener,
		HandleDice,
		HandleMove,

		HandleDiceSelect,
		HandleConfirm,
		HandleUndo,
		isLoaded,
	]);
	// socket events
	useEffect(() => {
		function onDisconnect() {
			setIsConnected(false);
			setInitialized(false);
			boardCleaner();
			console.log("socket disconnected");
		}
		function onConnect() {
			setIsConnected(true);
			console.log("connected");
		}

		matchSocket.on("disconnect", onDisconnect);
		matchSocket.on("connect", onConnect);
		matchSocket.on("disconnect", onDisconnect);

		matchSocket.on("disconnect", onDisconnect);
		matchSocket.on("exception", (error) => {
			console.log(error);
			// TODO: tell back to send error correct and send for react native
		});
		matchSocket.on("game-engine-event", (data: string) => {
			const socketData = JSON.parse(data);
			if (initialized && boardData) {
				switch ((socketData as SocketActionData).action) {
					case BackgammonSocketEventVariant.ROLLED_DICE: {
						const diceData = socketData as SocketActionData<DiceData>;
						HandleSocketRollDice(diceData.data.diceValues);
						break;
					}
					case BackgammonSocketEventVariant.DICE_SELECT: {
						const options =
							socketData as SocketActionData<SocketDiceSelectData>;
						const playerColor = getSquadFromPlayerId(
							options.data.playerId,
							boardData
						);

						sendMessage(playerColor, "DeactivePices");
						sendMessage("DiceManager", "DeactiveAllDice");
						sendMessage("DiceManager", "ActiveDice", options.data.selectedDice);
						sendMessage(
							playerColor,
							"PiceActive",
							options.data.moveOptions
								.map((option) => option.checkerId)
								.join(",")
						);

						break;
					}
					case BackgammonSocketEventVariant.PLAYER_MOVE: {
						const options = socketData as SocketActionData<SocketPiceMovedData>;

						const playerColor = getSquadFromPlayerId(
							options.data.playerId,
							boardData
						);

						if (
							options.data.move.toPosition.type === CheckerPositionVariants.OFF
						) {
							sendMessage(
								playerColor,
								"MovePice",
								`${options.data.checkerId},25`
							);
						} else {
							sendMessage(
								playerColor,
								"MovePice",
								`${options.data.checkerId},${options.data.move.toPosition.index}`
							);
						}
						sendMessage("DiceManager", "UseDice", options.data.selectedDice);
						options.data.pipCount.forEach((pipCount) => {
							const playerColor = getSquadFromPlayerId(
								pipCount.playerId,
								boardData
							);
							sendMessage(
								playerColor,
								"UpdatePipCount",
								pipCount.pipCount.toString()
							);
						});
						if (options.data.playerId === boardData.current_user_id) {
							sendMessage("DiceManager", "showUndoConfirmButton");
							sendMessage("DiceManager", "ActiveRandomDice");
						}
						break;
					}
					case BackgammonSocketEventVariant.UNDO: {
						const options = socketData as SocketActionData<SocketPiceUndoData>;

						const playerColor = getSquadFromPlayerId(
							options.data.playerId,
							boardData
						);

						if (
							options.data.move.move.fromPosition.type ===
							CheckerPositionVariants.OFF
						) {
							sendMessage(
								playerColor,
								"MovePice",
								`${options.data.move.move.checkerId},25`
							);
						}
						if (
							options.data.move.move.toPosition.type ===
							CheckerPositionVariants.OFF
						) {
							sendMessage(playerColor, "UndoOff");
						}
						if (
							options.data.move.move.fromPosition.type ===
							CheckerPositionVariants.BAR
						) {
							sendMessage(
								playerColor,
								"CapturePice",
								options.data.move.move.checkerId
							);
						} else {
							sendMessage(
								playerColor,
								"MovePice",
								`${options.data.move.move.checkerId},${options.data.move.move.fromPosition.index}`
							);
						}
						if (options.data.move.hitChecker) {
							const playerColor = getSquadFromPlayerId(
								options.data.move.hitChecker.playerId,
								boardData
							);
							sendMessage(
								playerColor,
								"MovePice",
								`${options.data.move.hitChecker.checkerId},${options.data.move.hitChecker.fromPosition.index}`
							);
						}
						sendMessage(
							"DiceManager",
							"UnUseDice",
							options.data.move.selectedDice
						);
						sendMessage("DiceManager", "DeactiveConfirmButton");
						sendMessage("DiceManager", "DeactiveAllDice");

						options.data.pipCount.forEach((pipCount) => {
							const playerColor = getSquadFromPlayerId(
								pipCount.playerId,
								boardData
							);
							sendMessage(
								playerColor,
								"UpdatePipCount",
								pipCount.pipCount.toString()
							);
						});

						matchSocket.emit("user-game-action", {
							match_id: matchID,
							action_name: "backgammon.select_dice",
							data: {
								player_id: boardData?.current_user_id,
								selected_dice: options.data.move.selectedDice,
							},
						});
						break;
					}
					case BackgammonSocketEventVariant.TURN_UPDATE: {
						const TurnData = socketData as SocketActionData<SocketTurnData>;

						HandleSocketTurnUpdate(TurnData.data.currentTurnPlayer.playerId);

						break;
					}
					case BackgammonSocketEventVariant.CAN_CONFIRM: {
						const result = socketData as SocketActionData<BackgammonPlayer>;
						if (result.data.playerId === boardData.current_user_id)
							sendMessage("DiceManager", "ActiveConfirmButton");

						break;
					}
					case BackgammonSocketEventVariant.END_GAME: {
						const gameResult = socketData as SocketActionData<SocketResultData>;
						sendMessage("Game Manager", "WinHandler");
						sendMessageToReactNative("endGame", { ...gameResult.data });

						break;
					}
					case BackgammonSocketEventVariant.TIME_OUT: {
						const result = socketData as SocketActionData<BackgammonPlayer>;

						const playerColor = getSquadFromPlayerId(
							result.data.playerId,
							boardData
						);
						sendMessage(
							playerColor ?? "",
							"SetTimeOuts",
							result.data.faultCount
						);

						break;
					}

					default:
						break;
				}
			}
		});
		MatchPresentsHandler();
		return () => {
			matchSocket.off();
		};
	}, [matchSocket, sendMessage, boardData, initialized]);

	const getProfile = (id: number) => {
		return boardData?.players_profiles.find((item) => item.user_id === id);
	};
	// initializer
	useEffect(() => {
		if (isLoaded && isConnected && boardData && !initialized) {
			sendMessage(
				"Game Manager",
				"setCurrentPlayer",
				boardData.currentPlayerColor
			);
			boardData.players.forEach((player) => {
				sendMessage(
					player.color,
					"GameInitializer",
					JSON.stringify({
						piceList: player.checkers,
						pipCount: player.pipCount,
						isCurrentPlayer: boardData.current_user_id === player.playerId,
					})
				);

				const profile = getProfile(player.playerId);

				sendMessage(
					player.color,
					"ActiveProfile",
					JSON.stringify({
						...profile,
						profile_image: profile?.profile_image.replace(".svg", ".png"),
						TimeOutsCount: player.faultCount,
						username: profile?.full_name ?? profile?.username,
						isCurrentPlayer: boardData.current_user_id === player.playerId,
					})
				);
			});
			const timer = Math.floor(boardData.remainingTime);
			HandleSocketTurnUpdate(boardData.LastTurn.playerId, timer);
			if (boardData.LastTurn.step === "MOVE_CHECKER") {
				HandleSocketRollDice(boardData.LastTurn.diceValues);

				if (boardData.LastTurn.playerId === boardData.current_user_id)
					sendMessage("DiceManager", "showUndoConfirmButton");
			} else if (
				boardData.LastTurn.step === "CONFIRM_TURN" &&
				boardData.LastTurn.playerId === boardData.current_user_id
			) {
				sendMessage("DiceManager", "showUndoConfirmButton");
				sendMessage("DiceManager", "ActiveConfirmButton");
			}

			setInitialized(true);
			sendMessageToReactNative("gameStarted", {});
		}
	}, [isLoaded, matchSocket, boardData, sendMessage, HandleSocketTurnUpdate]);
	return {
		loadingPercentage,
		unityProvider,
		isLoaded,
		isConnected,
		initialized,
	};
}
export default UseBackgammon;
